import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Lock, ArrowRight, Loader2, Check, FileText, Wand2, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * High-conversion paywall surfaced at the moment of intent on the Results page.
 * Shows the user the EXACT value of upgrading (count of rewrites + sections + cover letter)
 * and triggers Stripe Checkout in one click instead of redirecting to /pricing.
 *
 * Props:
 *  - improvementsCount: number of line-by-line improvements identified
 *  - sections: optional unique section list (e.g. ["Summary", "Experience", "Skills"])
 *  - variant: "inline" (full card, used inside the rewrites tab) | "compact" (one-row banner)
 */
export default function ProUnlockPaywall({
  improvementsCount = 0,
  sections = [],
  variant = "inline",
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    if (!user) {
      toast.error("Sign in first", { description: "Please sign in to upgrade to Pro." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/subscription", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout", {
        description: e?.response?.data?.detail || e.message,
      });
      setLoading(false);
    }
  };

  // Compact one-row banner — used to encourage upgrade on top of free-tier result
  if (variant === "compact") {
    return (
      <div
        data-testid="pro-unlock-paywall-compact"
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 lg:p-5 bg-zinc-900 text-white border-l-4 border-[#EF4444]"
      >
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-[#EF4444] shrink-0" strokeWidth={2.25} />
          <div className="text-sm">
            <span className="font-semibold">{improvementsCount > 0 ? `Unlock all ${improvementsCount} AI-rewritten sections` : "Unlock the full AI rewrite suite"}</span>
            <span className="text-zinc-400 mx-2">·</span>
            <span className="text-zinc-300">$20/mo · cancel anytime</span>
          </div>
        </div>
        <button
          onClick={startCheckout}
          disabled={loading}
          data-testid="pro-unlock-paywall-compact-cta"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#EF4444] hover:bg-white hover:text-zinc-900 text-white text-sm font-medium transition-colors disabled:opacity-60 shrink-0"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Upgrade <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    );
  }

  // Inline / full paywall — shown when user clicks into the AI Rewrites tab on free tier
  const uniqueSections = Array.from(new Set(sections.filter(Boolean))).slice(0, 6);
  const benefits = [
    { icon: Wand2, label: `${improvementsCount > 0 ? improvementsCount : "8+"} full AI-rewritten sections, copy-ready` },
    { icon: FileText, label: "Tailored cover letter generator (per job description)" },
    { icon: Download, label: "PDF export of every audit — recruiter-ready" },
    { icon: Sparkles, label: "Unlimited audits — score every new draft" },
  ];

  return (
    <div data-testid="pro-unlock-paywall" className="border border-zinc-200">
      {/* Hero band */}
      <div className="bg-zinc-900 text-white p-8 lg:p-12 relative overflow-hidden">
        <div className="absolute top-6 right-6 px-2 py-1 bg-[#EF4444] text-[10px] font-mono tracking-widest uppercase">
          POPULAR
        </div>
        <div className="overline text-zinc-400 mb-4">◆ THE FULL REWRITE SUITE</div>
        <h3 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tight leading-[1.05] mb-4">
          {improvementsCount > 0 ? (
            <>We can rewrite<br /><span className="text-[#EF4444]">{improvementsCount} specific sections</span> of your resume.</>
          ) : (
            <>Unlock the full<br /><span className="text-[#EF4444]">AI rewrite suite</span>.</>
          )}
        </h3>
        {uniqueSections.length > 0 && (
          <p className="text-zinc-400 text-sm mb-6" data-testid="pro-unlock-paywall-sections">
            Sections we'll rewrite for you: {uniqueSections.map((s, i) => (
              <span key={s}>
                <span className="text-white font-medium">{s}</span>
                {i < uniqueSections.length - 1 && <span className="text-zinc-600">, </span>}
              </span>
            ))}
          </p>
        )}

        {/* Faux teaser of what an unlocked rewrite looks like */}
        <div className="bg-white/5 border border-white/10 p-4 mt-4 mb-6 relative">
          <div className="overline text-zinc-500 mb-2">PREVIEW · LOCKED</div>
          <div className="font-mono text-xs text-zinc-300 leading-relaxed select-none" style={{ filter: "blur(4px)" }}>
            Drove $4.2M in net-new pipeline by re-architecting the onboarding flow,
            reducing time-to-value from 14 days to 38 hours and lifting Series-B
            referenceable accounts by 3.1x in two quarters…
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Lock className="w-6 h-6 text-white/70" strokeWidth={1.75} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <button
            onClick={startCheckout}
            disabled={loading}
            data-testid="pro-unlock-paywall-cta"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#EF4444] hover:bg-white hover:text-zinc-900 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Starting checkout...</>
            ) : (
              <>Unlock Pro — $20/month <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-white/20 text-zinc-300 hover:bg-white hover:text-zinc-900 text-sm transition-colors"
          >
            See full pricing
          </Link>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
          <span>Auto-renew · Cancel anytime · Secure checkout via Stripe</span>
        </div>
      </div>

      {/* Benefits grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
        {benefits.map(({ icon: Icon, label }) => (
          <div key={label} className="bg-white p-5 flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-[#002FA7] text-white shrink-0">
              <Icon className="w-3.5 h-3.5" strokeWidth={2} />
            </span>
            <div className="text-sm">
              <Check className="w-3.5 h-3.5 inline mr-1 text-[#007A5E]" />
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Social-proof footer */}
      <div className="bg-zinc-50 border-t border-zinc-200 px-6 lg:px-8 py-4 text-xs text-zinc-500 flex flex-col sm:flex-row gap-2 sm:gap-6 justify-between">
        <span>★ 120,000+ resumes scored · avg. <b className="text-zinc-900">+27 pts</b> after Pro rewrites</span>
        <span>Cancel anytime from the billing portal — no questions asked.</span>
      </div>
    </div>
  );
}
